// NOTE: need a way to keep the params (e.g. app=ios, USER_ID, etc)
// So that if the user logs out and go back to /signin, the params
// are still there.

import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, Redirect, useHistory } from 'react-router-dom';
import firebase from '../firebase';
import { signIn } from '../audiblogsApi';
import userStore from '../Stores/User';
import queryString from 'query-string';
import mixpanel from 'mixpanel-browser';
import logo from '../logo-no-text.png';
import loading from '../Assets/loading.gif';
import { observer } from 'mobx-react';
import { version } from '../../package.json';
import './Signin.scss';
import { setLandingPage } from '../utils/trackingParams';
import { usePendingActions } from '../hooks/usePendingActions';

const SignIn = observer(({ location, match }) => {
  const { logOut, user, firebaseUser, idToken } = userStore;
  const history = useHistory();
  
  // Handle pending coupons and referrals after signin
  usePendingActions();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [isNewUserSignup, setIsNewUserSignup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState('email'); // 'email', 'signin', 'signup'
  const [userExists, setUserExists] = useState(null);
  const hasTrackedSignup = useRef(false);
  const passwordInputRef = useRef(null);

  const params = useMemo(() => queryString.parse(location.search), [location.search]);
  const app = params.app;
  
  // Check if we're on the reset-password route
  const isResetPassword = location.pathname === '/reset-password';

  const handleGoogleSignIn = async () => {
    try {
      setIsLoading(true);
      setError('');
      const provider = new firebase.auth.GoogleAuthProvider();
      const result = await firebase.auth().signInWithPopup(provider);
      
      if (result.additionalUserInfo?.isNewUser && !hasTrackedSignup.current) {
        // Track successful signup (only once)
        hasTrackedSignup.current = true;
        mixpanel.track('Sign Up Success', {
          ...params,
          variant: params.redirected_from ? 'b' : 'a',
          source: params.source || 'direct',
          method: 'google'
        });
        
        // Calculate redirect URL for new user signup
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('redirected_from');
        searchParams.set('variant', params.redirected_from ? 'b' : 'a');
        if (params.redirected_from) {
          searchParams.set('redirected_from', params.redirected_from);
        }
        
        // Check for partner info in localStorage
        const pendingCoupon = localStorage.getItem('pendingCoupon');
        const pendingReferral = localStorage.getItem('pendingReferral');
        
        if (pendingCoupon) {
          try {
            const couponData = JSON.parse(pendingCoupon);
            searchParams.set('coupon', couponData.code);
            localStorage.removeItem('pendingCoupon');
          } catch (error) {
            console.error('Error parsing pending coupon:', error);
          }
        } else if (pendingReferral) {
          try {
            const referralData = JSON.parse(pendingReferral);
            searchParams.set('referral', referralData.uuid);
            localStorage.removeItem('pendingReferral');
          } catch (error) {
            console.error('Error parsing pending referral:', error);
          }
        }
        
        const queryString = searchParams.toString();
        const redirectUrl = queryString ? `/onboarding/ChooseVoice?${queryString}` : '/onboarding/ChooseVoice';
        
        setIsNewUserSignup(true);
        // Use window.location for immediate redirect
        window.location.href = redirectUrl;
        return;
      }

      if (result.user?.email) {
        const storedUtmParams = JSON.parse(localStorage.getItem('utm_params') || '{}');
        const storedUtmLandingPage = localStorage.getItem('utm_landingpage') || '';
        const distinctId = mixpanel.get_distinct_id();
        
        const existingLandingPage = mixpanel.get_property('utm_landingpage');
        const existingInitialLandingPage = mixpanel.get_property('initial_landingpage');
        
        const userProps = {
          email: result.user.email,
          name: result.user.displayName,
          button_clicked: params.source,
          ...storedUtmParams,
          utm_landingpage: storedUtmLandingPage || existingLandingPage,
          initial_landingpage: storedUtmLandingPage || existingInitialLandingPage
        };
        
        mixpanel.alias(distinctId);
        mixpanel.people.set(userProps);
        mixpanel.register_once(userProps);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailContinue = async (e) => {
    e.preventDefault();
    if (!email || !email.trim()) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      // Try to sign in with a dummy password to check if user exists
      // This will throw an error if user doesn't exist
      try {
        await firebase.auth().signInWithEmailAndPassword(email, 'dummy-password-for-check');
      } catch (error) {
        if (error.code === 'auth/user-not-found') {
          // User doesn't exist, show signup form
          setUserExists(false);
          setStep('signup');
          // Focus password input after state update
          setTimeout(() => passwordInputRef.current?.focus(), 0);
          return;
        } else if (error.code === 'auth/wrong-password') {
          // User exists but wrong password, show signin form
          setUserExists(true);
          setStep('signin');
          // Focus password input after state update
          setTimeout(() => passwordInputRef.current?.focus(), 0);
          return;
        } else {
          // Other error, show it
          setError(error.message);
          return;
        }
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError('');
      await firebase.auth().signInWithEmailAndPassword(email, password);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    
    try {
      setIsLoading(true);
      setError('');
      const result = await firebase.auth().createUserWithEmailAndPassword(email, password);
      
      if (result.additionalUserInfo?.isNewUser && !hasTrackedSignup.current) {
        hasTrackedSignup.current = true;
        mixpanel.track('Sign Up Success', {
          ...params,
          variant: params.redirected_from ? 'b' : 'a',
          source: params.source || 'direct',
          method: 'email'
        });
        
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('redirected_from');
        searchParams.set('variant', params.redirected_from ? 'b' : 'a');
        if (params.redirected_from) {
          searchParams.set('redirected_from', params.redirected_from);
        }
        
        const pendingCoupon = localStorage.getItem('pendingCoupon');
        const pendingReferral = localStorage.getItem('pendingReferral');
        
        if (pendingCoupon) {
          try {
            const couponData = JSON.parse(pendingCoupon);
            searchParams.set('coupon', couponData.code);
            localStorage.removeItem('pendingCoupon');
          } catch (error) {
            console.error('Error parsing pending coupon:', error);
          }
        } else if (pendingReferral) {
          try {
            const referralData = JSON.parse(pendingReferral);
            searchParams.set('referral', referralData.uuid);
            localStorage.removeItem('pendingReferral');
          } catch (error) {
            console.error('Error parsing pending referral:', error);
          }
        }
        
        const queryString = searchParams.toString();
        const redirectUrl = queryString ? `/onboarding/ChooseVoice?${queryString}` : '/onboarding/ChooseVoice';
        
        setIsNewUserSignup(true);
        window.location.href = redirectUrl;
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Please enter your email address');
      return;
    }
    
    // Redirect to the request password reset page with the email pre-filled
    history.push(`/request-password-reset?email=${encodeURIComponent(email)}`);
  };

  const handleBackToEmail = () => {
    setStep('email');
    setPassword('');
    setError('');
    setUserExists(null);
  };

  const redirectTo = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('redirected_from');
    
    let baseUrl = '/';
    if (isNewUserSignup) {
      baseUrl = `/onboarding/ChooseVoice`;
      searchParams.set('variant', params.redirected_from ? 'b' : 'a');
      if (params.redirected_from) {
        searchParams.set('redirected_from', params.redirected_from);
      }
    } else if (params.redirected_from === 'setup') {
      baseUrl = '/setup';
    } else if (params.redirected_from === 'newsletter') {
      baseUrl = '/newsletter';
      searchParams.set('article_url', params.article_url);
      searchParams.set('article_title', params.article_title);
    } else if (params.redirected_from === 'subscription') {
      baseUrl = '/subscription';
      searchParams.set('chars', params.chars);
    } else if (params.redirected_from === 'link') {
      baseUrl = '/link';
      searchParams.set('email', params.email);
    } else if (params.redirected_from === 'add') {
      baseUrl = '/add';
      searchParams.set('url', params.url);
    } else if (params.redirected_from === 'setupnew') {
      baseUrl = '/setupnew';
    } else if (params.redirected_from === 'trial') {
      baseUrl = '/add';
      searchParams.set('article_url', params.article_url);
    } else if (params.redirected_from === 'drawabox') {
      baseUrl = '/drawabox';
    } else if (params.redirected_from === 'onboarding') {
      baseUrl = '/onboarding/howtoclip';
    } else if (params.redirected_from === 'preferredVoices') {
      baseUrl = '/preferredVoices';
    } else if (params.redirected_from === 'onboarding/quickstart/2') {
      baseUrl = '/onboarding/quickstart/2';
    } else if (params.redirected_from === 'signup') {
      baseUrl = '/onboarding/ChooseVoice';
      if (params.variant) {
        searchParams.set('variant', params.variant);
      }
    }

    const queryString = searchParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
    }, [params, isNewUserSignup, location.search]);

  useEffect(() => {
    mixpanel.track('Open Signin Page', {
      ...params,
      variant: params.redirected_from ? 'b' : 'a',
      source: params.source || 'direct'
    });
  }, []);

  useEffect(() => {
    if (user) {
      if (params.redirected_from === 'drawabox') {
        mixpanel.track('Successful Login After Subscription');
      } else {
        setShouldRedirect(true);
      }
    }

    if (firebaseUser && !firebaseUser.email) {
      logOut();
      alert('Unable to get the email address. Please use a different sign-in method.');
    }
  }, [firebaseUser, user, idToken, match.params.USER_ID, logOut, redirectTo, params.redirected_from]);

  if (shouldRedirect || userStore.isSignedIn) {
    return <Redirect to={redirectTo} />;
  }

  if (params.app === 'ios') {
    return <Redirect to={`/shortcuts`} />;
  }

  if (userStore.isSignedIn === null) {
    return (
      <div className="signin-container">
        <img src={loading} style={{ height: '50px' }} alt="Loading..." />
      </div>
    );
  }

  return (
    <div className="signin-container">
      <img src={logo} className="logo" alt="Audioread logo" style={{ marginBottom: '30px' }} />
      
      <div className="signin-card">
        <h1>{isResetPassword ? 'Reset Your Password' : 'Welcome to Audioread'}</h1>

        {!isResetPassword && (
          <>
            <button onClick={handleGoogleSignIn} className="google-button" disabled={isLoading}>
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" />
              <span>Continue with Google</span>
            </button>

            <div className="divider">
              <span>or</span>
            </div>
          </>
        )}

        {isResetPassword && (
          <div style={{ marginBottom: '20px', textAlign: 'center' }}>
            <Link to="/signin" style={{ color: '#3b82f6', textDecoration: 'none' }}>
              ← Back to Sign In
            </Link>
          </div>
        )}

        {step === 'email' && !isResetPassword && (
          <form onSubmit={handleEmailContinue}>
            <div className="input-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                style={{ textAlign: 'left', width: '235px' }}
              />
            </div>

            {error && (
              <div style={{ color: '#ef4444', marginBottom: '20px', fontSize: '14px' }}>
                {error}
              </div>
            )}

            <button type="submit" className="signin-button" disabled={isLoading}>
              {isLoading ? (
                <div className="loading-spinner">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="spinner-path"/>
                  </svg>
                </div>
              ) : 'Continue'}
            </button>
          </form>
        )}

        {step === 'signin' && !isResetPassword && (
          <form onSubmit={handleEmailSignIn}>
            <div className="input-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                disabled
                style={{ textAlign: 'left', width: '235px', opacity: 0.7 }}
              />
            </div>

            <div className="input-group">
              <div className="password-header">
                <label htmlFor="password">Password</label>
                <button 
                  type="button"
                  onClick={handlePasswordReset}
                  className="forgot-password"
                  style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
                >
                  Forgot Password?
                </button>
              </div>
              <div className="password-input-container">
                <input
                  id="password"
                  ref={passwordInputRef}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  style={{ textAlign: 'left' }}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.99989 3L19.9999 21M9.8433 9.91364C9.32066 10.4536 8.99989 11.1892 8.99989 12C8.99989 13.6569 10.3431 15 11.9999 15C12.8031 15 13.5316 14.6741 14.0681 14.1471M6.49989 6.64715C4.59628 7.90034 3.15271 9.78394 2.45117 12C3.73989 16.0571 7.52989 19 11.9999 19C13.9313 19 15.7349 18.4194 17.2894 17.4184M10.9999 5.04939C11.3277 5.01673 11.6613 5 11.9999 5C16.4699 5 20.2599 7.94291 21.5486 12C21.2217 13.0177 20.7457 13.9615 20.1454 14.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 12C15 13.6569 13.6569 15 12 15C9 13.6569 9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2.45801 12C3.73228 7.94288 7.52257 5 12.0002 5C16.4778 5 20.2681 7.94291 21.5424 12C20.2681 16.0571 16.4778 19 12.0002 19C7.52256 19 3.73226 16.0571 2.45801 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div style={{ 
                color: error.includes('sent!') ? '#10b981' : '#ef4444', 
                marginBottom: '20px', 
                fontSize: '14px' 
              }}>
                {error}
              </div>
            )}

            <button type="submit" className="signin-button" disabled={isLoading}>
              {isLoading ? (
                <div className="loading-spinner">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="spinner-path"/>
                  </svg>
                </div>
              ) : 'Sign In'}
            </button>
          </form>
        )}

        {step === 'signup' && !isResetPassword && (
          <form onSubmit={handleEmailSignUp}>
            <div className="input-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                disabled
                style={{ textAlign: 'left', width: '235px', opacity: 0.7 }}
              />
            </div>

            <div className="input-group">
              <label htmlFor="password">Create Password</label>
              <div className="password-input-container">
                <input
                  id="password"
                  ref={passwordInputRef}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Create a password"
                  required
                  style={{ textAlign: 'left' }}
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3.99989 3L19.9999 21M9.8433 9.91364C9.32066 10.4536 8.99989 11.1892 8.99989 12C8.99989 13.6569 10.3431 15 11.9999 15C12.8031 15 13.5316 14.6741 14.0681 14.1471M6.49989 6.64715C4.59628 7.90034 3.15271 9.78394 2.45117 12C3.73989 16.0571 7.52989 19 11.9999 19C13.9313 19 15.7349 18.4194 17.2894 17.4184M10.9999 5.04939C11.3277 5.01673 11.6613 5 11.9999 5C16.4699 5 20.2599 7.94291 21.5486 12C21.2217 13.0177 20.7457 13.9615 20.1454 14.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15 12C15 13.6569 13.6569 15 12 15C9 13.6569 9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M2.45801 12C3.73228 7.94288 7.52257 5 12.0002 5C16.4778 5 20.2681 7.94291 21.5424 12C20.2681 16.0571 16.4778 19 12.0002 19C7.52256 19 3.73226 16.0571 2.45801 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div style={{ color: '#ef4444', marginBottom: '20px', fontSize: '14px' }}>
                {error}
              </div>
            )}

            <button type="submit" className="signin-button" disabled={isLoading}>
              {isLoading ? (
                <div className="loading-spinner">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="spinner-path"/>
                  </svg>
                </div>
              ) : 'Start for free'}
            </button>
          </form>
        )}

        {isResetPassword && (
          <form onSubmit={handlePasswordReset}>
            <div className="input-group">
              <label htmlFor="reset-email">Email</label>
              <input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                required
                style={{ textAlign: 'left', width: '235px' }}
              />
            </div>

            {error && (
              <div style={{ 
                color: error.includes('sent!') ? '#10b981' : '#ef4444', 
                marginBottom: '20px', 
                fontSize: '14px' 
              }}>
                {error}
              </div>
            )}

            <button type="submit" className="signin-button" disabled={isLoading}>
              {isLoading ? (
                <div className="loading-spinner">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="spinner-path"/>
                  </svg>
                </div>
              ) : 'Send Reset Email'}
            </button>
          </form>
        )}

        {(step === 'signin' || step === 'signup') && !isResetPassword && (
          <button 
            onClick={handleBackToEmail} 
            className="toggle-signin-link"
          >
            Use a different email
          </button>
        )}
      </div>

      <div className="footer">
        By continuing, you agree to our{' '}
        <Link to="/terms">Terms of Service</Link> and{' '}
        <Link to="/privacy">Privacy Policy</Link>
      </div>
    </div>
  );
});

export default SignIn;
